export const SITE_NAME = 'AllChinaBuy Spreadsheet';

export const SITE_FAVICON =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

export function siteOrigin(): string {
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (envUrl && envUrl.trim()) {
    return envUrl.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

export interface ArticleSeo {
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
}

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
  article?: ArticleSeo;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const ARTICLE_META_KEYS = ['article:published_time', 'article:modified_time', 'article:section'];

export function applySeo(data: SeoData): void {
  const origin = siteOrigin();
  const canonicalPath = data.path === '/' || data.path === '' ? '/' : data.path;
  const canonical = origin ? `${origin}${canonicalPath}` : canonicalPath;

  document.title = data.title;
  upsertMeta('name', 'description', data.description);
  if (data.keywords) {
    upsertMeta('name', 'keywords', data.keywords);
  } else {
    removeMeta('name', 'keywords');
  }
  upsertCanonical(canonical);

  upsertMeta('property', 'og:title', data.title);
  upsertMeta('property', 'og:description', data.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:type', data.type ?? 'website');
  upsertMeta('property', 'og:site_name', SITE_NAME);

  upsertMeta('name', 'twitter:card', data.image ? 'summary_large_image' : 'summary');
  upsertMeta('name', 'twitter:title', data.title);
  upsertMeta('name', 'twitter:description', data.description);

  if (data.image) {
    upsertMeta('property', 'og:image', data.image);
    upsertMeta('name', 'twitter:image', data.image);
  } else {
    removeMeta('property', 'og:image');
    removeMeta('name', 'twitter:image');
  }

  if (data.article) {
    if (data.article.publishedTime) {
      upsertMeta('property', 'article:published_time', data.article.publishedTime);
    } else {
      removeMeta('property', 'article:published_time');
    }
    if (data.article.modifiedTime) {
      upsertMeta('property', 'article:modified_time', data.article.modifiedTime);
    } else {
      removeMeta('property', 'article:modified_time');
    }
    if (data.article.section) {
      upsertMeta('property', 'article:section', data.article.section);
    } else {
      removeMeta('property', 'article:section');
    }
  } else {
    ARTICLE_META_KEYS.forEach((key) => removeMeta('property', key));
  }
}